import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowsLeftRight, ArrowDown, ArrowUp, CheckCircle, Database, Trash, LinkSimple } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function money(v) {
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v || 0));
}

export default function Reconciliation() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data, isLoading } = useQuery({
    queryKey: ["reconciliation"],
    queryFn: async () => (await api.get("/reconciliation")).data,
    refetchOnWindowFocus: false,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["reconciliation"] });
  const matchM = useMutation({
    mutationFn: async ({ txn_id, doc_type, doc_id }) => (await api.post("/reconciliation/match", { txn_id, doc_type, doc_id })).data,
    onSuccess: () => { toast.success("Matched & reconciled"); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const unmatchM = useMutation({
    mutationFn: async (txn_id) => (await api.post("/reconciliation/unmatch", { txn_id })).data,
    onSuccess: () => { toast.success("Match removed"); invalidate(); },
  });
  const seedM = useMutation({
    mutationFn: async () => (await api.post("/reconciliation/demo")).data,
    onSuccess: (r) => { toast.success(`Seeded ${r.seeded} demo transactions`); invalidate(); },
  });
  const clearM = useMutation({
    mutationFn: async () => (await api.delete("/reconciliation/demo")).data,
    onSuccess: (r) => { toast.success(`Cleared ${r.deleted} demo transactions`); invalidate(); },
  });

  const unrec = data?.unreconciled || [];
  const rec = data?.reconciled || [];

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="reconciliation-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight mt-1">Bank Reconciliation</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Match your synced bank transactions to sales invoices (money in) and purchase invoices (money out).
            Suggested matches are ranked by amount, reference and date — confirm with one click.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => seedM.mutate()} disabled={seedM.isPending} data-testid="recon-seed-demo"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-border text-xs font-semibold hover:bg-secondary disabled:opacity-50">
              <Database size={14} weight="bold" /> Seed demo
            </button>
            <button onClick={() => clearM.mutate()} disabled={clearM.isPending} data-testid="recon-clear-demo"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-border text-xs font-semibold hover:bg-secondary disabled:opacity-50">
              <Trash size={14} weight="bold" /> Clear demo
            </button>
          </div>
        )}
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!isLoading && unrec.length === 0 && rec.length === 0 && (
        <div className="border border-border rounded-sm p-10 text-center" data-testid="recon-empty">
          <ArrowsLeftRight size={36} className="text-muted-foreground mx-auto mb-3" />
          <p className="font-bold">No bank transactions to reconcile</p>
          <p className="text-sm text-muted-foreground mt-1">Connect a bank and load accounts, or seed demo transactions to try it out.</p>
        </div>
      )}

      {unrec.length > 0 && (
        <div className="space-y-3" data-testid="recon-unreconciled">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">To Reconcile ({unrec.length})</h2>
          <div className="space-y-3">
            {unrec.map(({ transaction: t, suggestions }) => {
              const credit = t.direction === "credit";
              return (
                <div key={t.id} className="border border-border rounded-sm bg-card p-4" data-testid="recon-txn-row">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center ${credit ? "bg-emerald-500/10" : "bg-secondary"}`}>
                        {credit ? <ArrowDown size={18} weight="bold" className="text-emerald-600" /> : <ArrowUp size={18} weight="bold" className="text-foreground" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{t.description || "Bank transaction"}</p>
                        <p className="text-xs text-muted-foreground">{t.date} · {credit ? "Money in" : "Money out"}{t.source === "demo" && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-primary font-semibold">Demo</span>}</p>
                      </div>
                    </div>
                    <p className={`tabular font-bold text-lg shrink-0 ${credit ? "text-emerald-600" : ""}`}>{money(t.amount)}</p>
                  </div>
                  <div className="mt-3 pl-12 space-y-1.5">
                    {suggestions.length === 0 && <p className="text-xs text-muted-foreground" data-testid="recon-no-match">No suggested match found.</p>}
                    {suggestions.map((s) => (
                      <div key={s.doc_id} className="flex items-center justify-between gap-3 bg-secondary/40 rounded-sm px-3 py-2" data-testid="recon-suggestion">
                        <div className="flex items-center gap-2 min-w-0 text-sm">
                          <LinkSimple size={13} className="text-muted-foreground shrink-0" />
                          <span className="font-mono font-semibold">{s.doc_no}</span>
                          <span className="text-muted-foreground truncate">{s.party}</span>
                          <span className="tabular">{money(s.amount)}</span>
                          {s.score >= 1.5 && <span className="text-[10px] uppercase tracking-wide text-primary font-semibold">Ref match</span>}
                        </div>
                        <button onClick={() => matchM.mutate({ txn_id: t.id, doc_type: s.type, doc_id: s.doc_id })} disabled={matchM.isPending}
                          data-testid="recon-confirm-match"
                          className="shrink-0 inline-flex items-center gap-1 h-7 px-3 rounded-sm bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50">
                          <CheckCircle size={13} weight="bold" /> Confirm
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {rec.length > 0 && (
        <div className="space-y-3" data-testid="recon-reconciled">
          <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Reconciled ({rec.length})</h2>
          <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-border bg-secondary/40">
                  <th className="py-2.5 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Transaction</th>
                  <th className="py-2.5 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold">Matched To</th>
                  <th className="py-2.5 px-4 text-xs uppercase tracking-wider text-muted-foreground font-semibold text-right">Amount</th>
                  <th className="py-2.5 px-4" />
                </tr>
              </thead>
              <tbody>
                {rec.map((t) => (
                  <tr key={t.id} className="border-b border-border/60" data-testid="recon-reconciled-row">
                    <td className="py-2.5 px-4"><span className="truncate block max-w-[260px]">{t.description}</span><span className="text-xs text-muted-foreground">{t.date}</span></td>
                    <td className="py-2.5 px-4"><span className="font-mono font-semibold">{t.matched_doc_no}</span> <span className="text-xs text-muted-foreground">({t.matched_type})</span></td>
                    <td className="py-2.5 px-4 text-right tabular font-semibold">{money(t.amount)}</td>
                    <td className="py-2.5 px-4 text-right">
                      <button onClick={() => unmatchM.mutate(t.id)} data-testid="recon-unmatch"
                        className="text-xs font-semibold text-muted-foreground hover:text-destructive">Unmatch</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
